import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { isValid } from 'swedish-personal-identity-number-validator';
import { StyledIcon, SvgSrc } from '~/components/shared/Icon/Icon';
import InputField from '~/components/shared/Input/InputField';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import { formatAmount } from '~/helpers/formatters.helper';
import useTranslate from '~/hooks/useTranslate';
import { Instalment } from '~/Interfaces';
import {
  PaymentMethodBodyContainer,
  PaymentMethodHeaderContainer,
  PaymentMethodMainContainer,
  SinglePaymentMethodContainer,
  StepCircle,
} from '~/pages/Factoring/Invoices/Invoice';
import {
  chooseSplitPaymentMethod,
  confirmingTermsAndConditions,
} from '~/store/actions/invoiceActions';
import { RootState } from '~/store/types/sharedTypes';
import { getPolicyTermsUrl } from '~/constants/markets';
import PaymentMethodsHeader from '../PaymentMethodsHeader';
import { CompanyOwnersContainer } from '../UBO/Ubo';
import Card from './Card';

export const InstalmentsBodyMain = styled.div`
  padding: 15px;
  background-color: white;
  border: 2px solid #f2f3f0;
  border-top: 0px;
  border-radius: 0px 0px 10px 10px;
`;

export const PaymentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 10px 0px 10px 0px;
`;

const InstalmentBody = styled.div`
  width: 100%;
  margin-top: 10px;
  border-radius 10px 10px 0px;
  font-size: 15px;
  cursor: auto;
`;
const InstalmentBodyHeader = styled.div`
  padding: 15px;
  text-align: center;
  background-color: white;
  border: 2px solid #f2f3f0;
  border-radius: 10px 10px 0px 0px;
`;
const BodyFooter = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  align-items: center;
  margin: auto;
`;
const TotalCost = styled.div`
  width: 100%;
  text-align: right;
  display: inline-grid;
  padding: 10px;
`;
export const ConfirmButton = styled.button`
  width: 100%;
  color: white;
  background-color: #214749
  border: none;
  border-radius: 10px;
  height: 47px;
  margin-top: 30px;
  font-size: 18px;
  margin-bottom: 10px;
  max-width: 300px;
  `;
export const PersonalNumber = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 300px;
  margin: auto;
`;
export const ErrorMessage = styled.div`
  width: 100%;
  padding-top: 10px;
  display: inherit;
`;
export const ErrorMessageIcon = styled.div`
  max-width: 15px;
  max-height: 15px;
`;
export const ErrorMessageText = styled.div`
  padding-left: 10px;
`;
export const Link = styled.a`
  cursor: pointer;
  color: #1387AC
  font-size: 12px;
  `;
export const TermsAndConditions = styled.div`
  display: inline;
  max-width: 400px;
`;

interface Props {
  onCancelButton: () => void;
  onConfirm: (personalNumber: string) => void;
  partPaymentPlan: Instalment[];
  invoiceId: string;
  withBankId?: boolean;
}

const InstalmentCards: React.FC<Props> = (props) => {
  const {
    onCancelButton,
    onConfirm,
    partPaymentPlan,
    invoiceId,
    withBankId,
  } = props;
  const dispatch = useDispatch();

  const [personalNumber, setPersonalNumber] = useState<string>('');
  const [personalNumberError, setPersonalNumberError] = useState<string>('');

  const translate = useTranslate();

  const {
    intl: { market, language },
  } = useSelector((state: RootState) => state);

  const onPersonalNumberChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPersonalNumberError('');
    setPersonalNumber(e.target.value);
  };

  const confirmTermsAndAndConditions = () => {
    if (personalNumber === '') {
      setPersonalNumberError(
        translate('Invoices.PersonalNumberError').toString()
      );
      return;
    }
    if (!isValid(personalNumber)) {
      setPersonalNumberError(
        translate('Invoices.PersonalNumberInvalidFormat').toString()
      );
      return;
    }
    setPersonalNumberError('');
    dispatch(confirmingTermsAndConditions(invoiceId, personalNumber));
    dispatch(
      chooseSplitPaymentMethod(invoiceId, 'part-payment', personalNumber, 3)
    );
    onConfirm(personalNumber);
  };

  const totalCost =
    +partPaymentPlan[0].annuityAmount +
    +partPaymentPlan[1].annuityAmount +
    +partPaymentPlan[2].annuityAmount;
  const totalFee =
    +partPaymentPlan[0].annuityInterest +
    +partPaymentPlan[1].annuityInterest +
    +partPaymentPlan[2].annuityInterest;

  return (
    <PaymentMethodMainContainer>
      <PaymentMethodsHeader
        text={translate('Back').toString()}
        onCancelButton={onCancelButton}
      />
      <PaymentMethodBodyContainer>
        <PaymentMethodHeaderContainer withSubtext>
          <Typography
            lineHeight={1.8}
            fontSize={1.2}
            color="#444444"
            fontWeight={FontWeights.Bold}
          >
            <StepCircle green />
            <StepCircle green={withBankId} />
            <StepCircle green={withBankId} />
            <StepCircle />
            <br />
            {withBankId
              ? translate('Invoices.SignWithBankID')
              : translate('Invoices.PayInThreeParts')}
          </Typography>
          <Typography
            color="#444444"
            fontWeight={FontWeights.Normal}
            fontSize={0.8}
            lineHeight={1.5}
          >
            {withBankId ? translate('Invoices.SignWithBankIDSubtext') : ' '}
          </Typography>
        </PaymentMethodHeaderContainer>
        <SinglePaymentMethodContainer>
          <InstalmentBody>
            <InstalmentBodyHeader>
              <Typography fontSize={1.2} fontWeight={FontWeights.Bold}>
                {translate('Invoices.PartPaymentPlan')}
              </Typography>
            </InstalmentBodyHeader>
            <InstalmentsBodyMain>
              <Card instalment={partPaymentPlan[0]} />
              <Card instalment={partPaymentPlan[1]} />
              <Card instalment={partPaymentPlan[2]} />
              <TotalCost>
                <Typography fontWeight={FontWeights.Thick} fontSize={1.6}>
                  {translate('Invoices.TotalCost')} {formatAmount(totalCost)}
                </Typography>
                <Typography fontWeight={FontWeights.Thin} fontSize={0.8}>
                  {`(${translate(
                    'Invoices.IncludesATotalFeeOf'
                  )} ${formatAmount(totalFee)})`}
                </Typography>
              </TotalCost>
            </InstalmentsBodyMain>
            <BodyFooter>
              {!withBankId && (
                <ConfirmButton onClick={() => onConfirm(personalNumber)}>
                  {translate('Invoices.Continue')}
                </ConfirmButton>
              )}
              {withBankId && (
                <CompanyOwnersContainer style={{ marginTop: '10px' }}>
                  <PersonalNumber>
                    <Typography fontWeight={FontWeights.Bold}>
                      {' '}
                      {translate('PersonalNumber')}{' '}
                    </Typography>
                    <InputField
                      type="text"
                      name="inputPersonalNumber"
                      placeholder="YYYYMMDD-XXXX"
                      disabled={false}
                      onChange={onPersonalNumberChanged}
                    />
                    {personalNumberError && (
                      <ErrorMessage>
                        <ErrorMessageIcon>
                          <StyledIcon
                            color="#EB4C6A"
                            icon={SvgSrc.WarningCircle}
                          />
                        </ErrorMessageIcon>
                        <ErrorMessageText>
                          {personalNumberError}
                        </ErrorMessageText>
                      </ErrorMessage>
                    )}
                    <TermsAndConditions>
                      <ConfirmButton onClick={confirmTermsAndAndConditions}>
                        {translate('Invoices.SignWithBankID')}
                      </ConfirmButton>
                      <Typography style={{ display: 'inline' }} fontSize={0.75}>
                        {' '}
                        {translate('Invoices.TermsAndConditionsText')}{' '}
                      </Typography>
                      {/* eslint-disable-next-line */}
                      <Link href="https://www.qred.se/info/villkor-delbetalning">
                        {' '}
                        {translate('Invoices.TermsAndConditions')}.
                      </Link>
                    </TermsAndConditions>
                    <TermsAndConditions style={{ marginTop: '12px' }}>
                      <Typography
                        style={{ display: 'inline', marginTop: '12px' }}
                        fontSize={0.75}
                      >
                        {' '}
                        {translate('Invoices.QredProcess')}{' '}
                      </Typography>
                      {/* eslint-disable-next-line */}
                      <Link href={getPolicyTermsUrl(market, language)}>
                        {' '}
                        {translate('Invoices.PrivacyPolicy')}.
                      </Link>
                      <Typography style={{ display: 'inline' }} fontSize={0.75}>
                        {' '}
                        {translate('Invoices.ReadPolicy')}{' '}
                      </Typography>
                    </TermsAndConditions>
                  </PersonalNumber>
                </CompanyOwnersContainer>
              )}
            </BodyFooter>
          </InstalmentBody>
        </SinglePaymentMethodContainer>
      </PaymentMethodBodyContainer>
    </PaymentMethodMainContainer>
  );
};

export default InstalmentCards;
